import { mpsToKmh, radToDeg } from '@rcs/domain';
import type { Frame } from '@rcs/physics';
import type { SimulationFrame } from '@rcs/physics';

/** HUD に表示する値（FR-ANA-001, 002）。単位は表示用（km/h・度・%）。 */
export interface HudValues {
  /** 経過時間 [s] */
  readonly time: number;
  /** 走行距離 [m] */
  readonly distance: number;
  /** 速度 [km/h] */
  readonly speedKmh: number;
  /** 高度 [m] */
  readonly altitude: number;
  /** 上下 G */
  readonly verticalG: number;
  /** 前後 G */
  readonly longitudinalG: number;
  /** 横 G */
  readonly lateralG: number;
  /** 合成 G（3 軸の大きさ） */
  readonly combinedG: number;
  /** 勾配 [%]（上りが正） */
  readonly gradePercent: number;
  /** バンク角 [度]（右へ傾く向きが正） */
  readonly bankDegrees: number;
}

/**
 * 姿勢から、バンク角 [rad] を求める。接線まわりの傾きで、上方 U が右（R = T × U）へ傾く向きが正
 * （物理仕様 2.2 節）。傾きが無ければ 0。
 */
export function bankAngle(orientation: Frame): number {
  return Math.atan2(-orientation.right[1], orientation.up[1]);
}

/** 勾配 [%]。接線の水平成分に対する高さ成分（上りが正）。鉛直（ループの頂点など）では ±∞ になる。 */
export function gradePercent(orientation: Frame): number {
  const [tx, ty, tz] = orientation.tangent;
  const horizontal = Math.hypot(tx, tz);
  return horizontal === 0 ? (ty >= 0 ? Infinity : -Infinity) : (ty / horizontal) * 100;
}

/** 結果のフレームから、HUD の値を作る。 */
export function hudValues(frame: SimulationFrame): HudValues {
  const { vertical, lateral, longitudinal } = frame.gForce;
  return {
    time: frame.time,
    distance: frame.s,
    speedKmh: mpsToKmh(frame.speed),
    altitude: frame.position[1],
    verticalG: vertical,
    longitudinalG: longitudinal,
    lateralG: lateral,
    combinedG: Math.hypot(vertical, lateral, longitudinal),
    gradePercent: gradePercent(frame.orientation),
    bankDegrees: radToDeg(bankAngle(frame.orientation)),
  };
}

/** 符号つきの小数（`+1.00`）。G は符号を見せる。 */
export function formatSigned(value: number, digits: number): string {
  if (!Number.isFinite(value)) return value > 0 ? '+∞' : '−∞';
  const text = value.toFixed(digits);
  // 丸めると 0 になる値は、符号をつけない（−0.00 と表示しない）
  if (Number(text) === 0) return (0).toFixed(digits);
  return value > 0 ? `+${text}` : text.replace('-', '−');
}

/** 勾配の表示。鉛直に近いときは「垂直」。 */
export function formatGrade(percent: number): string {
  if (!Number.isFinite(percent) || Math.abs(percent) > 9999)
    return percent > 0 ? '垂直（上り）' : '垂直（下り）';
  return `${formatSigned(percent, 0)} %`;
}
